// Knowledge Graph API service for DeepSeek integration
class KnowledgeGraphApiService {
  constructor() {
    this.apiKey = 'sk-7549b29d010a424a836d081a478552f4';
    this.apiUrl = 'https://api.deepseek.com/chat/completions';
  }

  // Robust JSON extraction method (reused from existing service)
  extractAndParseJSON(content) {
    if (!content || typeof content !== 'string') {
      return null;
    }

    // Method 1: Try direct JSON parsing first
    try {
      const parsed = JSON.parse(content.trim());
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (e) {
      // Continue to other methods
    }

    // Method 2: Look for JSON blocks in markdown code blocks
    const codeBlockMatch = content.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
    if (codeBlockMatch) {
      try {
        const parsed = JSON.parse(codeBlockMatch[1]);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Continue to other methods
      }
    }

    // Method 3: Look for JSON between curly braces
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Continue to other methods
      }
    }

    // Method 4: Look for JSON after "IMPORTANT:" or similar markers
    const importantMatch = content.match(/(?:IMPORTANT|RESPONSE|JSON):\s*(\{[\s\S]*\})/i);
    if (importantMatch) {
      try {
        const parsed = JSON.parse(importantMatch[1]);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Continue to other methods
      }
    }

    // Method 5: Try to find JSON in the last part of the content
    const lines = content.split('\n').reverse();
    for (const line of lines) {
      if (line.trim().startsWith('{') && line.trim().endsWith('}')) {
        try {
          const parsed = JSON.parse(line.trim());
          if (parsed && typeof parsed === 'object') {
            return parsed;
          }
        } catch (e) {
          // Continue to next line
        }
      }
    }

    // Method 6: Try to extract JSON from the entire content by removing explanatory text
    const cleanedContent = content
      .replace(/^[^{]*/, '') // Remove everything before first {
      .replace(/[^}]*$/, ''); // Remove everything after last }
    
    if (cleanedContent.startsWith('{') && cleanedContent.endsWith('}')) {
      try {
        const parsed = JSON.parse(cleanedContent);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Give up
      }
    }

    return null;
  }

  async analyzeTextForKnowledgeGraph(text) {
    const startTime = Date.now();
    let tokensUsed = 0;
    
    try {
      const prompt = `Analyze the following text and extract entities and relationships to create a knowledge graph. 

Text to analyze:
${text}

Please provide a comprehensive analysis in the following JSON format:

{
  "entities": {
    "characters": [
      {
        "id": "unique_id",
        "name": "Entity Name",
        "type": "protagonist|mentor|supporting_character|antagonist",
        "description": "Brief description of the character",
        "importance": 1-10,
        "tags": ["tag1", "tag2"]
      }
    ],
    "places": [
      {
        "id": "unique_id", 
        "name": "Place Name",
        "type": "dwelling|magical_realm|setting|landmark|city",
        "description": "Description of the place",
        "importance": 1-10,
        "tags": ["tag1", "tag2"]
      }
    ],
    "objects": [
      {
        "id": "unique_id",
        "name": "Object/Concept Name", 
        "type": "craft_tool|artwork|materials|virtue|knowledge|mystery|weapon|artifact|symbol",
        "description": "Description of the object or concept",
        "importance": 1-10,
        "tags": ["tag1", "tag2"]
      }
    ]
  },
  "relationships": [
    {
      "from": "source_entity_id",
      "to": "target_entity_id", 
      "type": "relationship_type",
      "strength": 1-10,
      "description": "Brief description of the relationship"
    }
  ],
  "analysis": {
    "total_entities": 0,
    "total_relationships": 0,
    "main_themes": ["theme1", "theme2"],
    "narrative_arc": "brief description of the story arc",
    "complexity_score": 1-10
  }
}

IMPORTANT: 
- Ensure all entity IDs are unique and descriptive
- Relationships should connect existing entities by their IDs
- Importance scores should reflect the entity's role in the narrative
- Strength scores should reflect the intensity/importance of relationships
- Provide realistic and meaningful relationships
- Focus on extracting the most important entities and relationships
- Ensure the JSON is valid and complete`;

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: 'You are an expert text analyst specializing in knowledge graph extraction. You provide detailed, accurate analysis in valid JSON format.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 4000
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content;
      
      if (!content) {
        throw new Error('No content received from API');
      }

      // Calculate tokens used (rough estimation)
      const inputTokens = Math.floor((prompt.length + text.length) / 4);
      const outputTokens = Math.floor(content.length / 4);
      tokensUsed = inputTokens + outputTokens;

      const parsedResult = this.extractAndParseJSON(content);
      
      if (!parsedResult) {
        console.warn('Failed to parse JSON from API response, using fallback');
        return this.createFallbackAnalysis(text);
      }

      // Add token information to the result
      const resultWithTokens = {
        ...parsedResult,
        metadata: {
          tokensUsed: tokensUsed,
          processingTime: Date.now() - startTime,
          inputTokens: inputTokens,
          outputTokens: outputTokens
        }
      };

      console.log('Knowledge Graph Analysis completed in', Date.now() - startTime, 'ms, Tokens used:', tokensUsed);
      return resultWithTokens;

    } catch (error) {
      console.error('Knowledge Graph Analysis failed:', error);
      return this.createFallbackAnalysis(text);
    }
  }

  createFallbackAnalysis(text) {
    // Create a basic fallback analysis when API fails
    const words = text.toLowerCase().split(/\s+/);
    const entities = {
      characters: [
        {
          id: 'main_character',
          name: 'Main Character',
          type: 'protagonist',
          description: 'The primary character in the narrative',
          importance: 8,
          tags: ['protagonist']
        }
      ],
      places: [
        {
          id: 'story_setting',
          name: 'Story Setting',
          type: 'setting',
          description: 'The main location where the story takes place',
          importance: 6,
          tags: ['setting']
        }
      ],
      objects: [
        {
          id: 'key_element',
          name: 'Key Element',
          type: 'mystery',
          description: 'An important element in the story',
          importance: 7,
          tags: ['important']
        }
      ]
    };

    const relationships = [
      {
        from: 'main_character',
        to: 'story_setting',
        type: 'inhabits',
        strength: 7,
        description: 'The main character exists in the story setting'
      },
      {
        from: 'main_character',
        to: 'key_element',
        type: 'interacts with',
        strength: 6,
        description: 'The main character interacts with the key element'
      }
    ];

    return {
      entities,
      relationships,
      analysis: {
        total_entities: 3,
        total_relationships: 2,
        main_themes: ['narrative', 'character development'],
        narrative_arc: 'A basic story structure',
        complexity_score: 5
      }
    };
  }

  // Convert API response to the format expected by the Knowledge Graph component
  convertToGraphData(apiResponse) {
    if (!apiResponse || !apiResponse.entities) {
      return { nodes: [], links: [] };
    }

    const allEntities = [
      ...(apiResponse.entities.characters || []),
      ...(apiResponse.entities.places || []),
      ...(apiResponse.entities.objects || [])
    ];

    // Convert entities to nodes
    const nodes = allEntities.map((entity, index) => {
      const angle = (index / allEntities.length) * 2 * Math.PI;
      const radius = 150 + Math.random() * 100;
      return {
        ...entity,
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        z: (Math.random() - 0.5) * 80,
        radius: Math.max(10, entity.importance * 2.2)
      };
    });

    // Convert relationships to links
    const links = (apiResponse.relationships || []).map(rel => ({
      ...rel,
      source: rel.from,
      target: rel.to,
      width: Math.max(2, rel.strength / 2)
    }));

    return { nodes, links };
  }
}

export default new KnowledgeGraphApiService(); 